import React from "react";

import { StreamlineIcon } from "src/components/icons";
import breadWheatIconUrl from "images/icons/bread-wheat.svg";
import ecologyLeafIconUrl from "images/icons/ecology-leaf.svg";
import fruitAcornIconUrl from "images/icons/fruit-acorn.svg";
import milkCartonIconUrl from "images/icons/milk-carton.svg";
import organicPlantGrow1IconUrl from "images/icons/organic-plant-grow-1.svg";

type KnownTagName = "vegan" | "vegan_on_request" | "contains_nuts"
  | "dairy_free_on_request" | "dairy_free" | "gluten_free" | "gluten_free_on_request"
  | "vegetarian" | "vegetarian_on_request";

interface GroupedTags {
  primary: string[];
  supplementary: string[];
}

export const buildTagElementsForTags = (tags: string[]) => {
  const tagElements = tags.reduce<GroupedTags>((accl, tag) => {
    const [scope, tagName]: string[] = tag.split(":");

    const normalisedTagName = tagName.replace(" ", "_").toLowerCase();

    if (scope == "core" && isKnownTag(normalisedTagName)) {
      return { primary: accl.primary.concat(tagName), supplementary: accl.supplementary };
    } else {
      return { primary: accl.primary, supplementary: accl.supplementary.concat(tagName) };
    }
  }, { primary: [], supplementary: [] });

  return [
    <ProductTags tags={tagElements.primary} isPrimary={true} key="primary" />,
    <ProductTags tags={tagElements.supplementary} isPrimary={false} key="supplementary" />,
  ]
}

export const ProductTags = ({
  tags,
  isPrimary
}: {
  tags: string[];
  isPrimary: boolean;
}): React.JSX.Element => {
  if (!isPrimary) {
    return <p className="is-tiny-text" data-testid="suppl-attributes">{tags.join(", ")}</p>;
  }

  const tagElements = tags.map(tag => {
    const normalisedTagName = tag.replace(" ", "_").toLowerCase();

    if (isKnownTag(normalisedTagName)) {
      const { icon, style, label } = knownTags[normalisedTagName];
      return (
        <div className={`badge ${style}`} key={`badge-${tag}`}>
          {icon}
          <span>{label}</span>
        </div>
      );
    }
  });

  return (
    <div className="attributes" data-testid="attributes">
      {tagElements}
    </div>
  );
}

type KnownTagConfigEntry = {
  icon: React.JSX.Element;
  style: string;
  label: string;
};
type KnownTagConfig = { [key in KnownTagName]: KnownTagConfigEntry; }

const knownTags: KnownTagConfig = {
  "vegan": {
    icon: <StreamlineIcon href={ecologyLeafIconUrl} />,
    style: "is-dietary-choice-vegan",
    label: "Vegan",
  },
  "vegan_on_request": {
    icon: <StreamlineIcon href={ecologyLeafIconUrl} />,
    style: "is-dietary-choice-vegan",
    label: "Vegan on request"
  },
  "contains_nuts": {
    icon: <StreamlineIcon href={fruitAcornIconUrl} />,
    style: "is-dietary-choice-contains-nuts",
    label: "Contains nuts",
  },
  "dairy_free_on_request": {
    icon: <StreamlineIcon href={milkCartonIconUrl} />,
    style: "is-dietary-choice-dairy-free",
    label: "Dairy free (on request)",
  },
  "dairy_free": {
    icon: <StreamlineIcon href={milkCartonIconUrl} />,
    style: "is-dietary-choice-dairy-free",
    label: "Dairy free",
  },
  "gluten_free": {
    icon: <StreamlineIcon href={breadWheatIconUrl} />,
    style: "is-dietary-choice-gluten-free",
    label: "Gluten free",
  },
  "gluten_free_on_request": {
    icon: <StreamlineIcon href={breadWheatIconUrl} />,
    style: "is-dietary-choice-gluten-free",
    label: "Gluten free (on request)",
  },
  "vegetarian": {
    icon: <StreamlineIcon href={organicPlantGrow1IconUrl} />,
    style: "is-dietary-choice-vegetarian",
    label: "Vegetarian",
  },
  "vegetarian_on_request": {
    icon: <StreamlineIcon href={organicPlantGrow1IconUrl} />,
    style: "is-dietary-choice-vegetarian",
    label: "Vegetarian (on request)",
  },
};

const isKnownTag = (tagName: string): tagName is KnownTagName => { return tagName in knownTags };
